/**
 * Posting one update to everything downstream — Step 154.
 *
 * Kai, 2026-09-14: *"if I add a comment to my problem, it should add replies to
 * all the tickets assigned, same with parent child tickets etc, and changes."*
 *
 * This is the thing a major incident exists to do. Forty people are affected by
 * one outage; the update goes on the problem once and reaches all forty, rather
 * than an agent pasting it forty times and getting it wrong on the eleventh.
 *
 * The rules live here — pure, tested, and shared — because the composer has to
 * show the SAME count it is about to act on. A browser that says "3 linked
 * tickets" and a server that writes to 5 is the bug this package exists to
 * prevent.
 *
 * ── Downstream is DIRECTED, and that is the whole safety of it ─────────
 *
 * Only two relationships propagate, and both point one way:
 *
 *  - **children** (`parent_id` = this ticket) — a parent's update is its
 *    children's business;
 *  - **associated incidents** (`associated_to_id` = this ticket) — the
 *    incidents belonging to this problem or change.
 *
 * `ticket_links` is deliberately NOT included. It is symmetric and untyped by
 * Step 21's design, so "linked" carries no direction — propagating along it
 * would push a problem's update sideways into unrelated tickets and, worse,
 * back up into whatever linked TO this one. A cascade with no direction is a
 * cascade with no brake.
 *
 * One level, for the same reason bundles are: a child that is itself a parent
 * does not re-broadcast. Recursive propagation across a graph somebody built by
 * hand is how one comment becomes three hundred emails.
 */

/** The `kind` values a message can be propagated as. */
export const PROPAGABLE_KINDS = ['public_reply', 'private_note'] as const;

/**
 * Only an agent's own words travel.
 *
 * A `requester_message` is one customer writing on their own ticket — copying
 * it onto somebody else's is putting words in their mouth on a ticket they
 * cannot see. A `system` message is already generated per ticket by whatever
 * caused it, so propagating one would duplicate it.
 */
export function canPropagateKind(kind: string): boolean {
  return (PROPAGABLE_KINDS as readonly string[]).includes(kind);
}

/** The bits of a candidate ticket that decide whether an update belongs on it. */
export interface PropagationCandidate {
  id: string;
  number: number;
  subject: string;
  trashedAt?: number | null;
  spamAt?: number | null;
  mergedIntoId?: string | null;
}

/**
 * Which candidates actually receive the update.
 *
 * Excluded, each for its own reason:
 *
 *  - **the source itself** — it already has the message; a self-referential
 *    parent or association is a data error we should survive, not repeat;
 *  - **binned or spam** — out of the queue by Step 111. Nobody is reading it,
 *    and a notification about a ticket somebody can no longer find is worse
 *    than silence;
 *  - **merged** — the message would land on a thread that redirects elsewhere,
 *    so it would be written where nobody looks. The merge target gets its own
 *    copy if it is genuinely downstream.
 *
 * De-duplicated by id: a child that is ALSO an associated incident is one
 * ticket, and posting to it twice is the obvious bug.
 */
export function propagationTargets(
  sourceId: string,
  candidates: PropagationCandidate[],
): PropagationCandidate[] {
  const out = new Map<string, PropagationCandidate>();
  for (const c of candidates) {
    if (c.id === sourceId) continue;
    if (c.trashedAt || c.spamAt) continue;
    if (c.mergedIntoId) continue;
    if (out.has(c.id)) continue;
    out.set(c.id, c);
  }
  return [...out.values()];
}

/**
 * Why this propagation cannot go ahead, or null if it can.
 *
 * Returns the sentence an agent reads. Checked on the server too — the UI
 * hiding a control is a courtesy, not an enforcement.
 */
export function propagationError(kind: string, targetCount: number): string | null {
  if (!canPropagateKind(kind)) {
    return 'Only replies and notes can be copied to linked tickets.';
  }
  if (targetCount < 1) {
    return 'This ticket has no child or linked tickets to copy to.';
  }
  return null;
}

/**
 * The composer's own words for what is about to happen.
 *
 * States the count and the KIND, because those are the two things that decide
 * whether this is safe. "Also post to 12 linked tickets" with no mention that
 * it is a public reply is how an internal sentence reaches twelve customers.
 */
export function propagationLabel(kind: string, targetCount: number): string {
  const noun = targetCount === 1 ? 'linked ticket' : 'linked tickets';
  const what = kind === 'private_note' ? 'this note' : 'this reply';
  return `Also add ${what} to ${targetCount} ${noun}`;
}
